import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Modal } from 'flowbite';

import EvaluationTemplateSummaryCompanyTable from './EvaluationTemplateSummaryCompanyTable';
import EvaluationTemplateSummaryWinner from './EvaluationTemplateSummaryWinner';

import { generateKey } from 'Helpers/misc';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';

export default function EvaluationTemplateSummary(props) {
    const colors = ['bg-purple-evaluation-companies', 'bg-blue-evaluation-companies'];

    const [isLoaded, setIsLoaded] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);

    const [template, setTemplate] = useState({});
    const [summary, setSummary] = useState([]);
    const [items, setItems] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [scorers, setScorers] = useState([]);
    const [itemsWeighting, setItemsWeighting] = useState({});

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [evaluationTemplateDeleteModalElement, setEvaluationTemplateDeleteModalElement] = useState(null);
    const evaluationTemplateDeleteModalRef = useRef();

    const cleanEvaluationTemplateDeleteModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const hideModalElement = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const successHandlerDelete = () => {
        hideModalElement(evaluationTemplateDeleteModalElement);
        window.location.href = props.redirectUrl;
    };

    const triggerDeleteOfficial = () => {
        setActionInProgress(true);
        axios
            .delete(props.deleteUrl)
            .then((res) => {
                setActionCompleted(true);
            })
            .catch((error) => {
                setActionFailed(true);
            });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (template.id) {
            setEvaluationTemplateDeleteModalElement(new Modal(evaluationTemplateDeleteModalRef.current, modalOptions(false)));
        }
    }, [template]);

    useEffect(() => {
        setIsCompleted(scorers.filter((scorer) => scorer.status !== 2).length === 0);
    }, [scorers]);

    const fetchData = () => {
        axios
            .get(props.fetchUrl)
            .then((response) => {
                setSummary(response.data.summary);
                setTemplate(response.data.template);
                setItems(response.data.items);
                setCompanies(response.data.companies);
                setScorers(response.data.scorers);
                setItemsWeighting(response.data.weightingBySection);
                setTimeout(() => {
                    setIsLoaded(true);
                }, 0);
            })
            .catch((e) => {
                console.log(e.response);
            });
    };

    const deleteEvaluationTemplate = () => {
        evaluationTemplateDeleteModalElement && evaluationTemplateDeleteModalElement.show();
    };

    return (
        <>
            {isLoaded ? (
                <div>
                    <h4>{template.name}</h4>
                    <div className={'tw-flex tw-justify-end tw-gap-1'}>
                        {!isCompleted && (
                            <Button variant="danger" data-dismiss="modal" onClick={deleteEvaluationTemplate}>
                                {$t('Delete')}
                            </Button>
                        )}
                        <Button as="a" variant="primary" href={props.pdfUrl} className="mr-4">
                            {$t('Download as pdf')}
                        </Button>
                    </div>
                    <EvaluationTemplateSummaryWinner data={summary.total ?? []} companies={companies} />

                    <div className="tw-space-y-8 tw-mt-8">
                        {companies.map((company, index) => {
                            return (
                                <EvaluationTemplateSummaryCompanyTable
                                    key={generateKey()}
                                    company={company}
                                    items={items}
                                    scorers={scorers}
                                    data={summary.companies[company.id] || {}}
                                    itemsWeighting={itemsWeighting}
                                    bgColor={colors[index % 2]}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div>Loading...</div>
            )}
            <ConfirmationModal
                closeHandler={() => {}}
                successHandler={successHandlerDelete}
                triggerAction={triggerDeleteOfficial}
                cleanStates={cleanEvaluationTemplateDeleteModalStates}
                allowAction={true}
                modalId={`delete-evaluation-template`}
                modalRef={evaluationTemplateDeleteModalRef}
                modalElement={evaluationTemplateDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t(
                    'Are you sure you want to delete {{name}}? The template has already been sent to scorers and by deleting the template all scores completed to date will be deleted and a new template will need to be created.',
                    { name: `Test` },
                )}
                actionCompletedMsg={$t('{{name}} deleted', { name: `Test` })}
                actionFailedMsg={$t('{{name}} could not be deleted', { name: `Test` })}
                title={$t('Delete Evaluation Template')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />
        </>
    );
}
